import { CompRef, Pointer, VariableData } from '@wix/document-services-types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';

type ThemeCustomProperty =
  | {
      type: string;
      value: string;
      displayName?: string;
    }
  | { error: string };

/**
 * Result of an editor-contributed text-theme resolution:
 * - `{ type: 'theme', label }` — a named theme is applied.
 * - `{ type: 'custom' }` — the selection is a custom style (no named theme).
 *
 * A resolver must return one of these explicitly. Absence of a resolver is a
 * separate case (the optional host method is simply not implemented), which the
 * caller detects and handles by falling back to its own default heuristic.
 */
export type TextThemeState =
  | { type: 'theme'; label: string }
  | { type: 'custom' };

export class ThemePlatformService extends AbstractComponentsPlatformService {
  public getThemeCustomProperties(filter?: { keys: string[] }) {
    const filterKeys = filter?.keys?.map((key) => {
      if (key.startsWith('--')) {
        return key.slice(2);
      }
      return key;
    });

    type SafeVariableData = VariableData & {
      cssVarName: string;
      _pointer: Pointer;
    };

    // The 'list' api currently returns all variables.
    // It would be helpful if DM could provide a way to return only cssProperties variable types - but
    // they don't have that kind of filter and refuse to add it for some reason at this point..
    const variables: SafeVariableData[] = this.dm.global.variables
      .list({})
      .map((v) => ({
        _pointer: v,
        ...this.dm.global.variables.get(v),
      }))
      .filter((v) => {
        if (typeof v.value.value !== 'string' || v.cssVarName === undefined) {
          return false;
        }

        return !filterKeys?.length || filterKeys?.includes(v.cssVarName);
      }) as SafeVariableData[]; // the filter confirms the cssVarName but ts doesn't know it

    const out: Record<string, ThemeCustomProperty> = {};

    // process variables one at a time, adding all children to the process list
    while (variables.length > 0) {
      const v = variables.shift();
      if (v === undefined) {
        break;
      }

      if (filterKeys?.length) {
        // add all child variables to the process list
        const withChildren = this.dm.global.variables.getChildVariables(
          // @ts-expect-error TBD fix
          v._pointer,
        );
        variables.push(
          ...(Object.values(withChildren.children).map((c) => ({
            _pointer: c.pointer,
            ...this.dm.global.variables.get(c.pointer),
          })) as SafeVariableData[]),
        );
      }

      if (out[v.cssVarName]) {
        continue;
      }

      if (v.cssVarName === undefined) {
        continue;
      }

      out[v.cssVarName] = {
        type: v.type,
        value: v.value.value as string,
        ...(v.name ? { displayName: v.name } : {}),
      };
    }

    // make sure all of the keys in the filter are in the result object
    if (filter?.keys) {
      for (let key of filter.keys) {
        if (key.startsWith('--')) {
          key = key.slice(2);
        }

        if (out[key]) {
          continue;
        }
        out[key] = { error: 'key not found' };
      }
    }

    return out;
  }

  /**
   * Resolve which text theme is applied to the current selection (or whether it
   * is a custom style). This logic is editor-specific, so it is delegated to the
   * host: the editor contributes the implementation via the host API. Returns
   * undefined when the host does not implement it, letting callers fall back to
   * their own heuristic.
   */
  public resolveTextThemeState(
    editorPointer: EditorPointer<CompRef>,
  ): TextThemeState | undefined {
    return this.hostAPI.platformApiMethods.editor.resolveTextThemeState?.(
      editorPointer,
    );
  }
}
